import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { AppEnities, CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowModule, WindowRef } from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faAddressCard, faBan, faCopy, faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { PlacementReviewStudent, PlacementReviewStudentNew, PlacementReviewStudentType } from '../../models/models.lib';
import { AssessmentService } from '../assessment.service';
import { AssessmentAddComponent } from '../assessment-add.component/assessment-add.component';

const { MY_REVIEW } = AppEnities;

@Component({
  selector: 'casi-assessment-list.component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './assessment-list.component.html',
  styleUrl: './assessment-list.component.scss'
})
export class AssessmentListComponent implements OnInit, OnDestroy {

  public view: Observable<PlacementReviewStudent[]> = new Observable<PlacementReviewStudent[]>();
  public rawData: PlacementReviewStudent[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: PlacementReviewStudent[] = filterBy(this.rawData, this.filter);

  public currentItem: PlacementReviewStudentType = PlacementReviewStudentNew.create({});

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = 10;

  // public formMode: FormModes = FormModes.NOTSET;

  public canUpdate: boolean = true;
  public canCreate: boolean = true;
  public canDestroy: boolean = true;
  public canView: boolean = true;

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;
  private _subs: Subscription[] = [];


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: AssessmentService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash,
      faBan, faCopy
    );
  }

  public sort: SortDescriptor[] = [];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_update, can_create, can_view, can_destroy } = this.me.allICanDo(MY_REVIEW);
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canView = can_view;
    this.canDestroy = can_destroy;
    // if (this.me.email)
    this.dataService.query();

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }


  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }


  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = PlacementReviewStudent.create(dataItem);
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.currentItem);
  }

  private openAddEditDialog(dataItem: PlacementReviewStudentType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: AssessmentAddComponent,
      minWidth: 320,
      width: 1000,
    });

    // const contentRef = dialogRef.content.instance as AssessmentAddComponent;
    // contentRef.title = 'EDIT ASSESSMENT';
    // contentRef.assessment = dataItem;
    //
    // dialogRef.result.subscribe((r: any) => {
    //   this.logger.debug('openAddEditDialog:result:', r);
    //   if (r instanceof DialogCloseResult) {
    //     return;
    //   }
    //   if (r.text === CasiCloseOptions.Saved) {
    //     this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
    //     this.handleClose(contentRef.editForm.value);
    //   }
    // });
  }

  private handleClose(data: any): void {
    this.logger.debug('handleClose...', data);
    this.save(data);
  }

  private save(assessment: any): void {
    this.logger.debug('save...', assessment);
    this.dataService.update(assessment.id, assessment)
      .then(resp => {
        this.logger.debug('save:update:resp: ', resp);
        this.notify("Assessment Updated");
        this.closeWindowRef();
        // if (this.me.email)
          this.dataService.query();
      })
      .catch(err => {
        this.logger.error('save:update:err: ', err);
        this.notify("Error Updating Assessment", true);
      });
  }


  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }


  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }
}

